/**
 * API文档结构化定义
 */

export const apiDefinitions = {
    translations: {
        title: "翻译任务",
        description: "上传文档，指定翻译的语言，创建新的翻译任务，并管理这些任务",
        endpoints: [
            {
                name: "创建翻译任务",
                method: "POST",
                path: "/api/v1/translations",
                description: "上传文档，指定翻译的语言，创建新的翻译任务",
                parameters: [
                    { name: "file", type: "file", required: true, description: `需要翻译的文件` },
                    { name: "fromLang", type: "string", required: true, description: `被翻译的语言` },
                    { name: "toLang", type: "string", required: true, description: `翻译成什么语言` },
                    { name: "glossary", type: "string", required: false, description: `指定的术语表名称，这里的名称是用户在网站上建立术语表时填写的名字` },
                    { name: "glossaryId", type: "string", required: false, description: `指定的术语表 ID，这个参数比 glossary 优先级更高：当两个参数都提供时，会使用这个参数指定的术语表` },
                    { name: "glossaryContent", type: "json", required: false, description: `直接传入术语表的内容，比 glossaryId 优先级更高。内容是由原文和对应译文组成的 key value JSON 对象，见"创建术语表"中的 content 参数说明` },
                    { name: "model", type: "string", required: false, description: `指定翻译模型。可以通过"查询可用模型"接口获取模型名称，如果不指定值的话，我们默认使用第一个。` },
                    { name: "clientTaskId", type: "string", required: false, description: `客户任务 ID，是 API 调用方提供的任务标识，长度不能超过 128，在同一个账户内必须唯一。可以用来查询翻译任务的状态，见"查询翻译任务"，适用于创建翻译任务时出现网络中断以致未能获取任务 ID 的情况` },
                    { name: "autoStart", type: "boolean|number", required: false, description: `默认是 true，也就是说，当积分足够时，任务创建完会自动开始全文翻译。可以传入 false，创建完后不会开始翻译；或者传入代表积分上限的数字，仅当所需积分**小于**这个数时，才会自动翻译` },
                    { name: "strictLanguageCheck", type: "boolean", required: false, description: `对源语言文本的检查，默认是 false，此时只是基于字符集来判断源语言种类，这样意味着一个英译中任务的文档中假设同时有英语和法语，可能两种语言都会被翻译成中文。传入 true，可以避免这种情况，但这样价格会高 50%，因为在翻译前要使用 LLM 逐句判断原文本的语言种类。` },
                    { name: "shouldTranslateImage", type: "boolean", required: false, description: `是否要翻译 PDF 文档中的图片，默认是 false，一般建议指定为 true。当指定为 true 时，会检测文件里的图片，并自动对含有图片的页进行 OCR 翻译。这样价格一般会高一些。具体参考"价格说明"。` },
                    { name: "shouldTranslateFileName", type: "boolean", required: false, description: `是否要翻译文件名，默认是 false。如果是 true，文件名也会被翻译成成目标语言` },
                    { name: "fastCreation", type: "boolean", required: false, description: `是否要快速创建任务并返回。默认是 false。如果指定为 true，服务器收到请求后，会先创建一个任务记录，然后立即返回记录的 ID 和 Analyzing 状态。分析文档和计算价格的工作，会异步在服务器端进行，分析完后会**自动扣积分启动任务**（仍然遵循 autoStart 的规则）。但如果指定了 previewCredits 或者传入了 webhookUrl，任务不会被自动执行，需要客户端自己通过 PUT 请求启动任务。` },
                    { name: "webhookUrl", type: "string", required: false, description: `获取任务状态变更通知的回调地址。如果提供了回调地址，当任务的状态和进度变更时，服务器端会自动发送 POST 请求，把 { id: <id>, status: <Status>, progress: <p> } 的 JSON 请求发送到回调地址中，其中 <p> 为从 0 ~ 100 的带小数的数字。` },
                    { name: "previewCredits", type: "number", required: false, description: `支付用于预览的积分。如果账户余额不足，则创建失败。服务端会自动根据积分计算需要翻译的 token 量进行翻译。如果用于预览的积分大于文档所需积分，则会完整翻译整篇文档。当传入 previewCredits 后，fastCreation 和 autoStart 都不再生效。` },
                    { name: "outputFileType", type: "string", required: false, description: `指定输出文档类型，仅用于图片翻译，现在图片翻译输出 PDF，可以指定为 JPG` },
                    { name: "forceOCR", type: "boolean", required: false, description: `如果指定为 true，则一定会先使用 OCR 识别文字然后再翻译，适用于正常翻译会出现全是乱码的情况` },
                    { name: "noResize", type: "boolean", required: false, description: `仅用于 PPTX，如果指定为 true，那么在生成 PPTX 的译文时，将不会尝试改变它里面的字号大小。` },
                    { name: "fileDescription", type: "string", required: false, description: `补充说明文档的情况，或者提出一些额外的翻译要求。例如：“专业术语不要翻译”。这些内容会给大模型参考。长度限制 140 字符` },
                    { name: "__experimental_temperature", type: "number", required: false, description: `指定 LLM 翻译时使用的 temperature 参数，必须大于 0 且小于 1` },
                    { name: "__experimental_translationAction", type: "string", required: false, description: `指示 LLM 翻译时使用的动词，可以是 Translate, Rewrite 或者 Revise。` },
                    { name: "__experimental_translationRule", type: "string", required: false, description: `建议 LLM 翻译时遵循的规则、风格等，建议用英语，小于 100 字符。` }
                ],
                responses: [
                    { 
                        status: 201, 
                        description: "成功创建翻译任务", 
                        fields: [
                            { name: "taskId", type: "string", description: "翻译任务的 ID，用于查询任务的状态和文件地址" },
                            { name: "price", type: "number", description: "翻译任务需要的积分（如果支付了预览积分，则这是除去预览之外剩下部分所需的积分）" },
                            { name: "totalPrice", type: "number", description: "翻译任务需要的总积分（price + previewCredits）" },
                            { name: "status", type: "string", description: "任务状态，可选值有 Analyzing, Unpaid, Waiting, PreviewProcessing, PreviewCompleted, Processing, Completed, Cancelled, Terminated, NotSupported。详细说明请参考文档中的状态描述部分" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 400, description: "参数不全" },
                    { status: 402, description: "积分余额不够" },
                    { status: 429, description: "调用太频繁" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "查询翻译任务",
                method: "GET",
                path: "/api/v1/translations/{id}",
                description: "根据任务 ID 查询翻译任务的状态（路径参数方式，推荐使用）",
                parameters: [
                    { name: "id", type: "string", required: true, description: `任务ID`, inPath: true },
                    { name: "withTexts", type: "boolean", required: false, description: `不需要值，如果提供了这个参数，则返回系统提取的一一对应的原文和译文，见在字段 "texts"，以及 revisedTexts（已使用的修订文本）` },
                    { name: "textsInArray", type: "boolean", required: false, description: `不需要值，如果提供了这个参数和 withTexts，那么对应的原文译文则是以数组格式返回，见 texts 字段的说明。` }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回任务信息", 
                        fields: [
                            { name: "taskId", type: "string", description: "翻译任务的 ID" },
                            { name: "status", type: "string", description: "任务状态，可能是 Analyzing, Unpaid, Waiting, PreviewProcessing, PreviewCompleted, Processing, Completed, Cancelled, Terminated, NotSupported。详细说明请参考文档中的状态描述部分" },
                            { name: "progress", type: "number", description: "进度，0 到 100，带小数的数字" },
                            { name: "originalFileUrl", type: "string", description: "原文档的 URL" },
                            { name: "translatedFileUrl", type: "string", description: "译文文档的 URL，翻译完成才有" },
                            { name: "bilingualFileUrl", type: "string", description: "双语文档的 URL，翻译完成才有" },
                            { name: "price", type: "number", description: "翻译任务需要的积分（如果支付了预览积分，则这是除去预览之外剩下部分所需的积分）" },
                            { name: "totalPrice", type: "number", description: "翻译任务需要的总积分（price + previewCredits）" },
                            { name: "totalToken", type: "number", description: "需要翻译的 token 总数" },
                            { name: "texts", type: "json", description: "如果在参数中指定了 withTexts，则返回系统提取的一一对应的原文和译文，以 JSON 的格式，默认是以 key-value 的格式，key 是原文，value 是译文" },
                            { name: "revisedTexts", type: "json", description: "如果在参数中指定了 withTexts，则用户主动上传用于重新生成文档的提取的修订译文，以 JSON 的格式" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 404, description: "ID 对应的翻译任务不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "查询翻译任务（参数方式）",
                method: "GET",
                path: "/api/v1/translations",
                description: "根据任务 ID 或客户任务 ID 来查询翻译任务的状态（查询参数方式，适用于需要使用客户任务ID的场景）",
                parameters: [
                    { name: "id", type: "string", required: false, description: `任务ID` },
                    { name: "clientTaskId", type: "string", required: false, description: `客户任务ID` },
                    { name: "withTexts", type: "boolean", required: false, description: `不需要值，如果提供了这个参数，则返回系统提取的一一对应的原文和译文，见在字段 "texts"，以及 revisedTexts（已使用的修订文本）` },
                    { name: "textsInArray", type: "boolean", required: false, description: `不需要值，如果提供了这个参数和 withTexts，那么对应的原文译文则是以数组格式返回，见 texts 字段的说明。` }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回任务信息", 
                        fields: [
                            { name: "taskId", type: "string", description: "翻译任务的 ID" },
                            { name: "status", type: "string", description: "任务状态，可能是 Analyzing, Unpaid, Waiting, PreviewProcessing, PreviewCompleted, Processing, Completed, Cancelled, Terminated, NotSupported。详细说明请参考文档中的状态描述部分" },
                            { name: "progress", type: "number", description: "进度，0 到 100，带小数的数字" },
                            { name: "originalFileUrl", type: "string", description: "原文档的 URL" },
                            { name: "translatedFileUrl", type: "string", description: "译文文档的 URL，翻译完成才有" },
                            { name: "bilingualFileUrl", type: "string", description: "双语文档的 URL，翻译完成才有" },
                            { name: "price", type: "number", description: "翻译任务需要的积分（如果支付了预览积分，则这是除去预览之外剩下部分所需的积分）" },
                            { name: "totalPrice", type: "number", description: "翻译任务需要的总积分（price + previewCredits）" },
                            { name: "totalToken", type: "number", description: "需要翻译的 token 总数" },
                            { name: "texts", type: "json", description: "如果在参数中指定了 withTexts，则返回系统提取的一一对应的原文和译文，以 JSON 的格式，默认是以 key-value 的格式，key 是原文，value 是译文" },
                            { name: "revisedTexts", type: "json", description: "如果在参数中指定了 withTexts，则用户主动上传用于重新生成文档的提取的修订译文，以 JSON 的格式" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 404, description: "ID 对应的翻译任务不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "删除翻译任务",
                method: "DELETE",
                path: "/api/v1/translations/{id}",
                description: "根据 ID 删除指定的翻译任务",
                parameters: [
                    { name: "id", type: "string", required: true, description: `任务ID`, inPath: true }
                ],
                responses: [
                    { status: 204, description: "成功删除任务" },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 404, description: "ID 对应的翻译任务不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "启动翻译任务",
                method: "PUT",
                path: "/api/v1/translations/{id}",
                description: "重新启动翻译任务，适用于积分不足或错误导致任务中断的情况",
                parameters: [
                    { name: "id", type: "string", required: true, description: `任务ID`, inPath: true },
                    { name: "revisedTexts", type: "json", required: false, description: `非必填。当翻译已完成后，可以用这个参数来提交人工修订的译文，以重新生成文件。revisedTexts 跟 GET 回来的 texts 的格式一样，也是原文译文一一对应的 key-value json。如 {"Bitcoin: A Peer-to-Peer Electronic Cash System":"比特币：点对点的电子现金系统"}。` },
                    { name: "showInvisibleText", type: "boolean", required: false, description: `显示不可见文字。非必填，可以是true。PDF 翻译时会检查文字的可见性，如果判断文字不可见，则不会显示它。但这个检查有可能会出错，如果遇到原文中可见的文字在译文中被隐藏了，可以使用这个参数来强制让它们出现。` },
                    { name: "fixBackground", type: "boolean", required: false, description: `修复 PDF 背景。非必填，可以是true。一般不需要使用。` }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功启动任务", 
                        fields: [
                            { name: "status", type: "string", description: "任务状态，可能是 Unpaid, Waiting, Processing, Completed, Cancelled, Terminated, NotSupported。详细说明请参考文档中的状态描述部分" },
                            { name: "progress", type: "number", description: "进度，0 到 100，带小数的数字" },
                            { name: "price", type: "number", description: "翻译任务需要的积分（如果支付了预览积分，则这是除去预览之外剩下部分所需的积分）" },
                            { name: "totalPrice", type: "number", description: "翻译任务需要的总积分（price + previewCredits）" },
                            { name: "totalToken", type: "number", description: "需要翻译的 token 总数" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 404, description: "ID 对应的翻译任务不存在" },
                    { status: 402, description: "积分余额不够" },
                    { status: 409, description: "文档仍然在分析中" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            }
        ]
    },

    glossaries: {
        title: "术语表管理",
        description: "创建和管理用于翻译的术语表",
        endpoints: [
            {
                name: "创建术语表",
                method: "POST",
                path: "/api/v1/glossaries",
                description: "添加新的术语表",
                parameters: [
                    { name: "name", type: "string", required: true, description: `术语表名称，不能跟用户之前创建的术语表名称重复` },
                    { name: "content", type: "json", required: true, description: `JSON 字符串，里面所有的 key 是原文，对应的 value 是译文，如 {"chain of thought": "思维链"}` }
                ],
                responses: [
                    { 
                        status: 201, 
                        description: "成功创建术语表", 
                        fields: [
                            { name: "id", type: "string", description: "术语表 ID，用于在翻译中指定术语表，或者查询、更改术语表信息" },
                            { name: "name", type: "string", description: "术语表名称" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 400, description: "参数不全或者不对" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "查询用户的所有术语表",
                method: "GET",
                path: "/api/v1/glossaries",
                description: "查询当前用户的所有术语表",
                parameters: [
                    { name: "offset", type: "number", required: false, description: `按照创建时间的逆序，从第几个开始返回，默认为 0` },
                    { name: "limit", type: "number", required: false, description: `一次返回多少条记录，默认为 20， 最大为 1000` }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回术语表列表", 
                        fields: [
                            { name: "data", type: "array", description: "数组，每条数据都是 { id, name } 包含术语表的 ID 和名称" },
                            { name: "count", type: "number", description: "该用户总共有多少个术语表" },
                            { name: "offset", type: "number", description: "这次是从第几个开始查询的" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "查询指定术语表",
                method: "GET",
                path: "/api/v1/glossaries/{id}",
                description: "根据 ID ，查询术语表",
                parameters: [
                    { name: "id", type: "string", required: true, description: `术语表ID`, inPath: true }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回术语表", 
                        fields: [
                            { name: "id", type: "string", description: "术语表的 ID" },
                            { name: "name", type: "string", description: "术语表的名称" },
                            { name: "content", type: "json", description: "术语表的内容，JSON 格式" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 404, description: "ID 对应的术语表不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "更改术语表",
                method: "PUT",
                path: "/api/v1/glossaries/{id}",
                description: "修改术语表的名称或者内容",
                parameters: [
                    { name: "id", type: "string", required: true, description: `术语表ID`, inPath: true },
                    { name: "name", type: "string", required: false, description: `术语表名称，不能跟用户之前创建的术语表名称重复` },
                    { name: "content", type: "json", required: false, description: `JSON 字符串，里面所有的 key 是原文，对应的 value 是译文，如 {"chain of thought": "思维链"}` }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功更新术语表", 
                        fields: [
                            { name: "id", type: "string", description: "术语表 ID" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 400, description: "参数不对" },
                    { status: 404, description: "ID 对应的术语表不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "删除术语表",
                method: "DELETE",
                path: "/api/v1/glossaries/{id}",
                description: "删除指定术语表",
                parameters: [
                    { name: "id", type: "string", required: true, description: `术语表ID`, inPath: true }
                ],
                responses: [
                    { status: 204, description: "成功删除术语表" },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 404, description: "ID 对应的术语表不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            }
        ]
    },

    info: {
        title: "信息查询",
        description: "查询各种可用的翻译参数",
        endpoints: [
            {
                name: "查询可用语言",
                method: "GET",
                path: "/api/v1/languages",
                description: "返回可用语言的值，用在创建翻译任务中的 fromLang 和 toLang 参数中",
                parameters: [
                    { name: "names", type: "boolean", required: false, description: `如果提供了这个参数（不需要值），则返回值会包含中文名` }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回可用语言列表",
                        fields: [
                            { name: "array", type: "array", description: "字符串的数组，每个字符串代表一种语言，用于翻译时指定语言，如\"Simplified Chinese\"；如果请求中提供了 names 参数，则是返回包含 { name, value } 的数组，其中 name 是该语言的中文名，如\"简体中文\"，value 是用于翻译时指定语言的值。" }
                        ]
                    }
                ]
            },
            {
                name: "查询可用文件扩展名",
                method: "GET",
                path: "/api/v1/filetypes",
                description: "返回支持的文件扩展名，如 .pdf 之类的",
                parameters: [],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回可用文件扩展名",
                        fields: [
                            { name: "array", type: "array", description: "字符串的数组，每个字符串代表一种文件的扩展名，如 .pdf, .docx 等" }
                        ]
                    }
                ]
            },
            {
                name: "查询可用模型",
                method: "GET",
                path: "/api/v1/modelNames",
                description: "返回支持的模型名称",
                parameters: [
                    { name: "withDetails", type: "boolean", required: false, description: "如果提供了这个参数（不需要值），返回带详情的模型信息" }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回可用模型列表",
                        fields: [
                            { name: "array", type: "array", description: "字符串的数组，每个字符串代表一个模型名；如果提供了 withDetails 参数，则返回模型详情对象的数组" }
                        ]
                    }
                ]
            }
        ]
    },

    account: {
        title: "账户信息",
        description: "获取账户的余额和基本信息",
        endpoints: [
            {
                name: "获取账户信息",
                method: "GET",
                path: "/api/v1/me",
                description: "查询当前用户的账户信息，目前只返回余额",
                parameters: [],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回账户信息", 
                        fields: [
                            { name: "balance", type: "number", description: "积分余额" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            }
        ]
    },

    team: {
        title: "团队管理",
        description: "管理用户团队信息和成员操作",
        endpoints: [
            {
                name: "获取团队信息",
                method: "GET",
                path: "/api/v1/me/team",
                description: "获取当前用户的团队信息。如果用户是团队管理员，返回团队完整信息和所有成员列表；如果是普通成员，只返回团队基本信息和自己的成员信息",
                parameters: [],
                responses: [
                    { 
                        status: 200, 
                        description: "成功返回团队信息", 
                        fields: [
                            { name: "team", type: "object", description: "团队信息对象，包含 id, name, code, closed, memberCount, remainingCredits 字段。如果用户没有团队则为 null" },
                            { name: "isOwner", type: "boolean", description: "当前用户是否为团队所有者/管理员" },
                            { name: "members", type: "array", description: "团队成员数组。管理员可看到所有成员，普通成员只能看到自己。每个成员包含 id, user, consumedCredits, translatedDocuments, remark 字段" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "修改团队成员备注",
                method: "PUT",
                path: "/api/v1/me/team/member/{id}",
                description: "修改指定团队成员的备注信息。只有团队所有者才能执行此操作",
                parameters: [
                    { name: "id", type: "string", required: true, description: "团队成员ID", inPath: true },
                    { name: "remark", type: "string", required: true, description: "新的备注内容" }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功更新成员备注", 
                        fields: [
                            { name: "success", type: "boolean", description: "操作是否成功" },
                            { name: "message", type: "string", description: "操作结果消息" },
                            { name: "member", type: "object", description: "更新后的成员信息，包含完整的用户和统计数据" }
                        ] 
                    },
                    { status: 400, description: "请求参数错误或格式不正确" },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 403, description: "只有团队所有者可以执行此操作" },
                    { status: 404, description: "指定的团队成员不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            },
            {
                name: "移除团队成员",
                method: "DELETE",
                path: "/api/v1/me/team/member/{id}",
                description: "从团队中移除指定成员。只有团队所有者才能执行此操作。移除后该成员将无法再使用团队积分",
                parameters: [
                    { name: "id", type: "string", required: true, description: "团队成员ID", inPath: true }
                ],
                responses: [
                    { 
                        status: 200, 
                        description: "成功移除团队成员", 
                        fields: [
                            { name: "success", type: "boolean", description: "操作是否成功" },
                            { name: "message", type: "string", description: "操作结果消息" },
                            { name: "removedMember", type: "object", description: "被移除成员的基本信息，包含 userId 和 teamId" }
                        ] 
                    },
                    { status: 401, description: "没有提供正确的 API 密钥" },
                    { status: 403, description: "只有团队所有者可以执行此操作" },
                    { status: 404, description: "指定的团队成员不存在" },
                    { status: 500, description: "服务器端出现的其他错误" }
                ]
            }
        ]
    }
};

// 翻译任务状态描述
export const translationStatusDescriptions = {
    Analyzing: "任务正在分析中，系统正在分析文档并计算翻译所需积分",
    Unpaid: "任务创建完成但未支付积分，需要启动翻译；进入这个状态有可能是因为积分不足，或者创建时 autoStart=false",
    Waiting: "任务已加入队列等待处理",
    PreviewProcessing: "任务预览正在处理中",
    PreviewCompleted: "任务预览已完成，需要支付剩余积分继续翻译",
    Processing: "任务正在处理中",
    Completed: "任务已完成",
    Cancelled: "任务已被后台取消，通常是临时操作",
    Terminated: "任务因错误或其他原因被终止",
    NotSupported: "文档格式不支持或文档内容无法处理"
};

// 示例代码生成器
export const generateCodeExamples = (endpoint, language) => {
    const { method, path, parameters } = endpoint;
    const apiPath = path.replace(/\{([^}]+)\}/g, (_, param) => `$\{${param}}`);

    // 生成不同语言的示例代码
    switch (language) {
        case 'curl':
            return generateCurlExample(method, apiPath, parameters);
        case 'python':
            return generatePythonExample(method, apiPath, parameters);
        case 'typescript':
            return generateTypeScriptExample(method, apiPath, parameters);
        case 'java':
            // Java 使用原始 path，自己处理路径参数
            return generateJavaExample(method, path, parameters);
        default:
            return '';
    }
};

// cURL示例
function generateCurlExample(method, path, parameters) {
    let url = `https://translate.simplifyai.cn${path}`;
    
    // 对于 GET 请求，将查询参数添加到 URL 中
    if (method === 'GET') {
        const queryParams = parameters.filter(p => !p.inPath).map(p => {
            // 对于 boolean 类型的参数，如果不需要值则只使用参数名
            if (p.type === 'boolean' && p.description && p.description.includes('不需要值')) {
                return p.name;
            }
            return `${p.name}=value`;
        }).join('&');
        if (queryParams) {
            url += `?${queryParams}`;
        }
    }
    
    let command = `curl -X ${method} "${url}"`;

    // 添加认证头
    command += ' \\\n  -H "Authorization: Bearer at-YOUR_API_KEY"';

    if (['POST', 'PUT'].includes(method)) {
        // 所有POST/PUT请求都使用FormData
        command += ' \\\n  -H "Content-Type: multipart/form-data"';
        parameters.forEach(param => {
            if (!param.inPath) {
                if (param.type === 'file') {
                    command += ` \\\n  -F "${param.name}=@/path/to/your/file.pdf"`;
                } else if (param.type === 'json') {
                    command += ` \\\n  -F "${param.name}='{\"key\":\"value\"}'"`;
                } else if (param.type === 'boolean') {
                    command += ` \\\n  -F "${param.name}=false"`;
                } else if (param.type === 'number') {
                    command += ` \\\n  -F "${param.name}=0"`;
                } else {
                    command += ` \\\n  -F "${param.name}=value"`;
                }
            }
        });
    }

    return command;
}

// Python示例
function generatePythonExample(method, path, parameters) {
    let code = `import requests

# API密钥
api_key = "at-YOUR_API_KEY"

# API端点
url = f"https://translate.simplifyai.cn${path}"

# 请求头
headers = {
    "Authorization": f"Bearer {api_key}"
}
`;

    if (['POST', 'PUT'].includes(method)) {
        // 所有 POST/PUT 请求都使用 FormData
        code += `
# 请求数据`;
        
        const hasFile = parameters.some(p => p.type === 'file' && !p.inPath);
        if (hasFile) {
            code += `
files = {`;
            parameters.forEach(param => {
                if (!param.inPath && param.type === 'file') {
                    code += `
    "${param.name}": open("/path/to/your/file.pdf", "rb"),`;
                }
            });
            code += `
}`;
        }
        
        code += `
data = {`;
        parameters.forEach(param => {
            if (!param.inPath && param.type !== 'file') {
                if (param.type === 'json') {
                    code += `
    "${param.name}": '{"key": "value"}',`;
                } else {
                    code += `
    "${param.name}": "value",`;
                }
            }
        });
        code += `
}

# 发送请求
response = requests.${method.toLowerCase()}(url, headers=headers${hasFile ? ', files=files' : ''}, data=data)
`;
    } else {
        // GET请求
        code += `
# 请求参数
params = {`;
        parameters.forEach(param => {
            if (!param.inPath) {
                code += `
    "${param.name}": "value",`;
            }
        });
        code += `
}

# 发送请求
response = requests.${method.toLowerCase()}(url, headers=headers, params=params)
`;
    }

    code += `
# 处理响应
if response.status_code == ${method === 'DELETE' ? 204 : 200}:
    print("请求成功")
    ${method === 'DELETE' ? '' : 'print(response.json())'}
else:
    print(f"请求失败: {response.status_code}")
    print(response.text)
`;

    return code;
}

// TypeScript示例
function generateTypeScriptExample(method, path, parameters) {
    let code = `import axios from 'axios';

// API密钥
const apiKey = 'at-YOUR_API_KEY';

// API端点
const url = \`https://translate.simplifyai.cn${path}\`;

// 请求头
const headers = {
  'Authorization': \`Bearer \${apiKey}\`
};

`;

    if (['POST', 'PUT'].includes(method)) {
        // 所有 POST/PUT 请求都使用 FormData
        code += `// 使用FormData提交数据
const formData = new FormData();`;
        parameters.forEach(param => {
            if (!param.inPath) {
                if (param.type === 'file') {
                    code += `
// 假设我们有一个文件输入元素 <input type="file" id="fileInput">
// const fileInput = document.getElementById('fileInput') as HTMLInputElement;
// formData.append('${param.name}', fileInput.files[0]);
formData.append('${param.name}', /* 文件对象 */);`;
                } else if (param.type === 'json') {
                    code += `
formData.append('${param.name}', JSON.stringify({"key": "value"}));`;
                } else if (param.type === 'boolean') {
                    code += `
formData.append('${param.name}', 'false');`;
                } else if (param.type === 'number') {
                    code += `
formData.append('${param.name}', '0');`;
                } else {
                    code += `
formData.append('${param.name}', 'value');`;
                }
            }
        });

        code += `

// 发送请求
const sendRequest = async () => {
  try {
    const response = await axios.${method.toLowerCase()}(url, formData, { headers });
    console.log('请求成功:', response.data);
  } catch (error) {
    console.error('请求失败:', error);
  }
};

sendRequest();
`;
    } else {
        // GET或DELETE请求
        if (parameters.some(p => !p.inPath)) {
            code += `// 请求参数
const params = {`;
            parameters.forEach(param => {
                if (!param.inPath) {
                    code += `
  ${param.name}: ${param.type === 'number' ? 0 : param.type === 'boolean' ? 'false' : '"value"'},`;
                }
            });
            code += `
};

`;
        }

        code += `// 发送请求
const sendRequest = async () => {
  try {
    const response = await axios.${method.toLowerCase()}(url, { 
      headers${parameters.some(p => !p.inPath) ? ',\n      params' : ''}
    });
    console.log('请求成功:', ${method === 'DELETE' ? 'response.status' : 'response.data'});
  } catch (error) {
    console.error('请求失败:', error);
  }
};

sendRequest();
`;
    }

    return code;
}

// Java示例
function generateJavaExample(method, path, parameters) {
    const hasPathParams = parameters.some(p => p.inPath);
    let pathWithParams = path;

    if (hasPathParams) {
        pathWithParams = path.replace(/\{([^}]+)\}/g, (match, p1) => `" + ${p1} + "`);
    }

    let code = `import java.io.File;
import java.io.IOException;
import org.apache.http.HttpEntity;
import org.apache.http.client.methods.*;
import org.apache.http.entity.ContentType;
import org.apache.http.entity.StringEntity;
import org.apache.http.entity.mime.MultipartEntityBuilder;
import org.apache.http.impl.client.CloseableHttpClient;
import org.apache.http.impl.client.HttpClients;
import org.apache.http.util.EntityUtils;
import org.apache.http.client.utils.URIBuilder;

public class ApiExample {
    public static void main(String[] args) {
        // API密钥
        String apiKey = "at-YOUR_API_KEY";
        
        // 创建HttpClient实例
        try (CloseableHttpClient httpClient = HttpClients.createDefault()) {`;

    if (method === 'GET') {
        code += `
            // 构建URI和请求参数
            URIBuilder builder = new URIBuilder("https://translate.simplifyai.cn${pathWithParams}");`;

        // 添加GET参数
        parameters.forEach(param => {
            if (!param.inPath) {
                code += `
            builder.setParameter("${param.name}", "value");`;
            }
        });

        code += `
            
            // 创建GET请求
            HttpGet request = new HttpGet(builder.build());
            request.setHeader("Authorization", "Bearer " + apiKey);`;
    } else if (method === 'DELETE') {
        code += `
            // 创建DELETE请求
            HttpDelete request = new HttpDelete("https://translate.simplifyai.cn${pathWithParams}");
            request.setHeader("Authorization", "Bearer " + apiKey);`;
    } else if (method === 'POST' || method === 'PUT') {
        // 所有 POST 或 PUT 请求都使用多部分表单
        code += `
            // 创建${method === 'POST' ? 'POST' : 'PUT'}请求
            ${method === 'POST' ? 'HttpPost' : 'HttpPut'} request = new ${method === 'POST' ? 'HttpPost' : 'HttpPut'}("https://translate.simplifyai.cn${pathWithParams}");
            request.setHeader("Authorization", "Bearer " + apiKey);
            
            // 构建多部分请求体
            MultipartEntityBuilder builder = MultipartEntityBuilder.create();`;

        parameters.forEach(param => {
            if (!param.inPath) {
                if (param.type === 'file') {
                    code += `
            File file = new File("/path/to/your/file.pdf");
            builder.addBinaryBody("${param.name}", file, ContentType.APPLICATION_OCTET_STREAM, file.getName());`;
                } else if (param.type === 'json') {
                    code += `
            builder.addTextBody("${param.name}", "{\\"key\\":\\"value\\"}", ContentType.APPLICATION_JSON);`;
                } else {
                    code += `
            builder.addTextBody("${param.name}", "value");`;
                }
            }
        });

        code += `
            
            // 设置请求实体
            HttpEntity entity = builder.build();
            request.setEntity(entity);`;
    }

    code += `
            
            // 发送请求并处理响应
            try (CloseableHttpResponse response = httpClient.execute(request)) {
                int statusCode = response.getStatusLine().getStatusCode();
                HttpEntity entity = response.getEntity();
                
                if (statusCode >= 200 && statusCode < 300) {
                    System.out.println("请求成功");
                    ${method === 'DELETE' ? '' : 'System.out.println(EntityUtils.toString(entity));'}
                } else {
                    System.out.println("请求失败: " + statusCode);
                    if (entity != null) {
                        System.out.println(EntityUtils.toString(entity));
                    }
                }
            }
        } catch (Exception e) {
            e.printStackTrace();
        }
    }
}
`;

    return code;
} 